import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiActivityLog,
  ApiActivityLogEventType,
  ApiActivityLogListResponse,
  getProjectActivityLogQueryKey,
  getPropertyActivityLogQueryKey,
} from '@kaiizen/kai-pro-api';
import { useCachedQuery } from '@kaiizen/react-persistent-cache';

import { useKaiProApiClient } from '../useKaiProApiClient';

type ActivityLogPageRequest = { limit: number; cursor?: string; eventTypes?: readonly ApiActivityLogEventType[] };

/** One activity log, newest first: the first page cached under `queryKey`, later pages appended by `loadMore`. */
const usePagedActivityLog = (
  queryKey: string,
  fetchPage: ((request: ActivityLogPageRequest) => Promise<ApiActivityLogListResponse>) | undefined,
  { limit, eventTypes }: Omit<ActivityLogPageRequest, 'cursor'>,
) => {
  const kaiProApiClient = useKaiProApiClient();
  const query = useCachedQuery<ApiActivityLogListResponse>(
    queryKey,
    () => fetchPage!({ limit, eventTypes }),
    { enabled: !!kaiProApiClient.token && !!fetchPage, staleAgeMs: 0 },
  );
  const [entries, setEntries] = useState<ApiActivityLog[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    setEntries(query.data?.entries ?? []);
    setNextCursor(query.data?.nextCursor);
  }, [queryKey, query.data]);

  const loadMore = useCallback(async () => {
    if (!fetchPage || !nextCursor || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const nextPage = await fetchPage({ limit, cursor: nextCursor, eventTypes });
      setEntries(current => [...current, ...nextPage.entries]);
      setNextCursor(nextPage.nextCursor);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [fetchPage, limit, nextCursor, eventTypes]);

  return {
    ...query,
    entries,
    nextCursor,
    hasMore: !!nextCursor,
    isLoadingMore,
    loadMore,
  };
};

/** A project's activity log, newest first; `eventTypes` narrows it to entries of those types. */
export const useProjectActivityLog = (projectId?: string | null, options?: { limit?: number; eventTypes?: readonly ApiActivityLogEventType[] }) => {
  const kaiProApiClient = useKaiProApiClient();
  const limit = options?.limit ?? 20;
  const eventTypes = options?.eventTypes;
  const fetchPage = useCallback(
    (request: ActivityLogPageRequest) => kaiProApiClient.getProjectActivityLog(projectId!, request),
    [kaiProApiClient, projectId],
  );
  return usePagedActivityLog(getProjectActivityLogQueryKey(projectId, limit, eventTypes), projectId ? fetchPage : undefined, { limit, eventTypes });
};

/**
 * The activity of every project at a property, archived ones included, newest first; `eventTypes`
 * narrows it (see `projectLifecycleActivityEventTypes` for a property's job history).
 */
export const usePropertyActivityLog = (orgPropertyId?: string | null, options?: { limit?: number; eventTypes?: readonly ApiActivityLogEventType[] }) => {
  const kaiProApiClient = useKaiProApiClient();
  const limit = options?.limit ?? 20;
  const eventTypes = options?.eventTypes;
  const fetchPage = useCallback(
    (request: ActivityLogPageRequest) => kaiProApiClient.getPropertyActivityLog(orgPropertyId!, request),
    [kaiProApiClient, orgPropertyId],
  );
  return usePagedActivityLog(getPropertyActivityLogQueryKey(orgPropertyId, limit, eventTypes), orgPropertyId ? fetchPage : undefined, {
    limit,
    eventTypes,
  });
};
